import { request } from "$/lib/api"
import {
  Company,
  CompanyContactPerson,
  CreateCompany,
  CreateCompanyContactPerson,
  CreateDepartment,
  Department,
  MoveDepartment,
  UpdateCompany,
  UpdateCompanyContactPerson,
  UpdateDepartment,
} from "@repo/common"
import { Route, populateRoute } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

async function fetchCompanies(storeId: string): Promise<Company[]> {
  return await request<Company[]>({
    url: populateRoute(Route.COMPANY__FIND_MANY, storeId),
    method: "GET",
  })
}

async function fetchCompany(companyId: string): Promise<Company> {
  return await request<Company>({
    url: populateRoute(Route.COMPANY__FIND_ONE, companyId),
    method: "GET",
  })
}

async function createCompany(
  storeId: string,
  data: CreateCompany,
): Promise<Company> {
  return await request<Company>({
    url: populateRoute(Route.COMPANY__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function updateCompany(
  companyId: string,
  data: UpdateCompany,
): Promise<Company> {
  return await request<Company>({
    url: populateRoute(Route.COMPANY__UPDATE, companyId),
    method: "PATCH",
    data,
  })
}

async function deleteCompany(companyId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.COMPANY__DELETE, companyId),
    method: "DELETE",
  })
}

async function createDepartment(
  companyId: string,
  data: CreateDepartment,
): Promise<Department> {
  return await request<Department>({
    url: populateRoute(Route.DEPARTMENT__CREATE, companyId),
    method: "POST",
    data,
  })
}

async function updateDepartment(
  departmentId: string,
  data: UpdateDepartment,
): Promise<Department> {
  return await request<Department>({
    url: populateRoute(Route.DEPARTMENT__UPDATE, departmentId),
    method: "PATCH",
    data,
  })
}

async function deleteDepartment(departmentId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.DEPARTMENT__DELETE, departmentId),
    method: "DELETE",
  })
}

async function moveDepartment(
  departmentId: string,
  data: MoveDepartment,
): Promise<Department> {
  return await request<Department>({
    url: populateRoute(Route.DEPARTMENT__MOVE, departmentId),
    method: "PATCH",
    data,
  })
}

async function createContactPerson(
  companyId: string,
  data: CreateCompanyContactPerson,
): Promise<CompanyContactPerson> {
  return await request<CompanyContactPerson>({
    url: populateRoute(Route.COMPANY_CONTACT_PERSON__CREATE, companyId),
    method: "POST",
    data,
  })
}

async function updateContactPerson(
  contactPersonId: string,
  data: UpdateCompanyContactPerson,
): Promise<CompanyContactPerson> {
  return await request<CompanyContactPerson>({
    url: populateRoute(Route.COMPANY_CONTACT_PERSON__UPDATE, contactPersonId),
    method: "PATCH",
    data,
  })
}

async function deleteContactPerson(contactPersonId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.COMPANY_CONTACT_PERSON__DELETE, contactPersonId),
    method: "DELETE",
  })
}

const companyQueries = {
  all: () => ["companies"],
  lists: () => [...companyQueries.all(), "list"],
  list: (storeId: string) =>
    queryOptions({
      queryKey: [...companyQueries.lists(), storeId],
      queryFn: () => fetchCompanies(storeId),
    }),
  detail: (companyId: string) =>
    queryOptions({
      queryKey: [...companyQueries.all(), "detail", companyId],
      queryFn: () => fetchCompany(companyId),
    }),
}

export function useCompanyMutations(storeId: string) {
  const queryClient = useQueryClient()
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries(companyQueries.list(storeId)),
      queryClient.invalidateQueries({ queryKey: companyQueries.all() }),
      queryClient.invalidateQueries({ queryKey: ["jobs"] }),
      queryClient.invalidateQueries({ queryKey: ["calendar"] }),
    ])
  }

  return {
    createMutation: useMutation({
      mutationFn: (data: CreateCompany) => createCompany(storeId, data),
      onSettled: invalidate,
    }),
    updateMutation: useMutation({
      mutationFn: ({
        companyId,
        data,
      }: { companyId: string; data: UpdateCompany }) =>
        updateCompany(companyId, data),
      onSettled: invalidate,
    }),
    deleteMutation: useMutation({
      mutationFn: (companyId: string) => deleteCompany(companyId),
      onSettled: invalidate,
    }),
    createDepartmentMutation: useMutation({
      mutationFn: ({
        companyId,
        data,
      }: { companyId: string; data: CreateDepartment }) =>
        createDepartment(companyId, data),
      onSettled: invalidate,
    }),
    updateDepartmentMutation: useMutation({
      mutationFn: ({
        departmentId,
        data,
      }: { departmentId: string; data: UpdateDepartment }) =>
        updateDepartment(departmentId, data),
      onSettled: invalidate,
    }),
    deleteDepartmentMutation: useMutation({
      mutationFn: (departmentId: string) => deleteDepartment(departmentId),
      onSettled: invalidate,
    }),
    moveDepartmentMutation: useMutation({
      mutationFn: ({
        departmentId,
        data,
      }: { departmentId: string; data: MoveDepartment }) =>
        moveDepartment(departmentId, data),
      onSettled: invalidate,
    }),
    createContactPersonMutation: useMutation({
      mutationFn: ({
        companyId,
        data,
      }: { companyId: string; data: CreateCompanyContactPerson }) =>
        createContactPerson(companyId, data),
      onSettled: invalidate,
    }),
    updateContactPersonMutation: useMutation({
      mutationFn: ({
        contactPersonId,
        data,
      }: {
        contactPersonId: string
        data: UpdateCompanyContactPerson
      }) => updateContactPerson(contactPersonId, data),
      onSettled: invalidate,
    }),
    deleteContactPersonMutation: useMutation({
      mutationFn: (contactPersonId: string) =>
        deleteContactPerson(contactPersonId),
      onSettled: invalidate,
    }),
  }
}

export default companyQueries
